import { service } from '@ember/service';
import { t } from 'ember-intl';
import { trackedFunction } from 'reactiveweb/function';
import Component from '@glimmer/component';
import matchMedia from 'tio-common/helpers/match-media';
import type { Company as CompanyModel } from 'tio-data/data/resources/company.type';
import type IntlService from 'ember-intl/services/intl';
import type PreRegisterService from 'tio-employee/services/pre-register';
import type DemoModeService from 'tio-employee/services/demo-mode';
// This is for types. Anything we have custom may show an error.
import type { Store as StoreService } from '@warp-drive/core';

const defaultName = 'Tuition.io';

export interface AppHeaderBannerSignature {
  Args: {
    company?: CompanyModel;
  };
  Element: HTMLDivElement;
}

export default class AppHeaderBannerComponent extends Component<AppHeaderBannerSignature> {
  @service declare store: StoreService;
  @service declare intl: IntlService;
  @service declare preRegister: PreRegisterService;
  @service declare demoMode: DemoModeService;

  get company() {
    if (this.demoMode.isActive) {
      return null;
    }

    return this.companyState?.value;
  }

  get companyName() {
    return this.company?.name || this.preRegister.companyName || defaultName;
  }

  get programTitle() {
    return (
      this.company?.programTitle ||
      this.preRegister.programTitle ||
      this.intl.t('header.banner.default_program_name')
    );
  }

  get logoUrl() {
    return this.preRegister.logo;
  }

  companyState = trackedFunction<Promise<CompanyModel | null>>(this, async () => {
    const companyId = this.args.company?.id;

    // `companyId` will only exist if the session is authenticated,
    //  otherwise we return `null` and fallback on the `preRegister` service
    //  for company banner values.
    return companyId
      ? // @ts-expect-error: fix model type
        this.store.peekRecord<CompanyModel>('company', companyId, { include: 'logo' })
      : null;
  });

  <template>
    <div
      id="header-title"
      class="tio-row-flush md:items-start justify-start md:flex-col grow text-wrap"
      ...attributes
    >
      <img
        src={{this.logoUrl}}
        class="h-10 max-w-32 object-contain"
        alt={{this.companyName}}
        title={{this.companyName}}
      />
      {{#if (matchMedia "(min-width: 400px)")}}
        <div class="text-xs tio-fg-secondary">
          {{t
            "header.banner.tagline"
            programTitle=this.programTitle
            brandClass="tio-header-banner-brand"
            htmlSafe=true
          }}
        </div>
      {{/if}}
    </div>
  </template>
}
